import React, { useState, useEffect } from "react"
import { Form, Input, Label, FormGroup, Container } from "reactstrap"
import { Trans, useLingui } from "@lingui/react"
import { t } from "@lingui/macro"
import { v4 as uuid } from "uuid"
import { useSelector, useDispatch } from "react-redux"
import { createVslaError, checkGroupName } from "../../store/actions/createVsla"
import { setCurrentPage } from "../../store/reducers/navigation"
import validateInput from "../../view/shared/validations/addGroup"
import { defaultGroupSettings, getCountryName } from "../../view/shared/helpers"
import StellarLoader from "../layout/StellarLoader"
import ProjectSelector from "../layout/MultiSelector"
import LocationForm from "../layout/Location/LocationForm"

import CustomAlert from "../layout/CustomAlert"
import { triggerAction } from "../../store/constants"
import { GROUP_PATCH_LOCAL } from "../../store/reducers/base/group"
import PropTypes from "prop-types"
const ErrorMessage = ({ msg }) => {
  return (
    <CustomAlert color="danger">
      {msg || <Trans id="notifications.group_create_error" />}
    </CustomAlert>
  )
}

ErrorMessage.propTypes = {
  msg: PropTypes.string,
}

const headerStyle = {
  color: "#E36F1E",
  fontFamily: "quicksand",
  fontWeight: "bolder",
  fontSize: "1.8em",
  textAlign: "center",
  paddingBottom: "0.5em",
}
const radioTextStyle = {
  fontFamily: "Fira Sans",
  fontSize: "18px",
}
const formStyle = {
  flex: "10",
  padding: "5px",
  fontFamily: "Fira Sans",
  fontSize: "20px",
  height: "50px",
}

const AddGroup = (props) => {
  const { i18n } = useLingui()
  const dispatch = useDispatch()
  const {
    isConnected,
    user,
    isCreating,
    createVslaError: vslaError,
    projects,
    stateGroup,
    vsla,
    defaultCountry,
    transportedLocationData,
  } = useSelector((state) => ({
    isConnected: state.isConnected,
    user: state.user,
    stateGroup: state.group,
    vsla: state.vsla,
    isCreating: state.isCreatingVsla,
    createVslaError: state.createVslaError,
    projects: state.projects,
    defaultCountry: state.countries.defaultCountry,
    transportedLocationData: state.locationTransport,
  }))
  const [state, setState] = useState({
    name: "",
    share_value: "",
    vsla_uuid: uuid(),
    sec_id: "",
    location: "",
    savings_cycle: "",
    country: getCountryName(localStorage.getItem("_country")),
    projects: [],
    currency: "",
    establishment_date: "",
    secretary_loan_service_charge: true,
    errors: {},
    showLocationError: false,
    selectedLocations: {},
  })

  useEffect(() => {
    dispatch(createVslaError())
    setState((prevState) => ({
      ...prevState,
      sec_id: user.id,
      ...(stateGroup
        ? {
          savings_cycle: stateGroup.cycle_duration,
          name: stateGroup.name,
          share_value: vsla?.metadata?.share_value,
          establishment_date: stateGroup.establishment_date,
          country: stateGroup?.country,
        }
        : {}),
    }))
  }, [])

  const onChange = (e) => {
    const { name, value } = e.target
    if (name === "name") {
      dispatch(createVslaError())
    }

    if (name === "country" && ["لبنان", "Lebanon"].includes(value)) {
      setState((prevState) => ({
        ...prevState,
        share_value: 1,
        savings_cycle: "12",
      }))
    } else if (name === "country" && ["لبنان", "Lebanon"].includes(state.country)) {
      setState((prevState) => ({
        ...prevState,
        share_value: "",
        establishment_date: "",
      }))
    }
    if (name === "secretary_loan_service_charge") {
      const isTrueSet = value === "true"
      setState((prevState) => ({ ...prevState, [name]: isTrueSet }))
      return
    }
    setState((prevState) => ({ ...prevState, [name]: value }))
    // this will remove errors and set status to pending
    dispatch(triggerAction(GROUP_PATCH_LOCAL, {}))
  }

  const isValid = () => {
    const { errors, isValid } = validateInput(state)

    if (!isValid) {
      setState((prevState) => ({ ...prevState, errors }))
    }

    return isValid
  }

  const filterProjectsByCountry = () => {
    const { projects: selectedProjects } = state
    const filteredIds = projects
      .filter(
        (project) =>
          project.country === transportedLocationData.country.name &&
          selectedProjects.includes(project.id),
      )
      .map((project) => project.id)
    return filteredIds
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    try {
      if (isValid()) {
        if (!transportedLocationData.valid) {
          setState((prevState) => ({ ...prevState, showLocationError: true }))
          setTimeout(() => {
            setState((prevState) => ({ ...prevState, showLocationError: false }))
          }, 4000)
          return
        }
        const blockChain = {
          description: state.name,
          metadata: {
            status: "Active",
            share_value: parseFloat(state.share_value),
            sec_id: parseInt(state.sec_id),
            vsla_uuid: state.vsla_uuid,
            welfareCashOutType: "Share based",
            membershipCashOutType: "Refund",
            currency: state.currency,
            fineCashOutType: "Share based",
            loanInterestCashOutType: defaultGroupSettings["loanInterestCashOutType"],
            savingsCashOutType: defaultGroupSettings["savingsCashOutType"],
          },
        }
        const validProjectIds = filterProjectsByCountry()
        delete transportedLocationData.valid
        const group = {
          created_at: new Date().toISOString(),
          locality: transportedLocationData.locality
            ? transportedLocationData.locality.name
            : "",
          description: state.name,
          name: state.name,
          blockchain_key: null,
          blockchain_wallet_id: state.vsla_uuid,
          longi: "",
          lati: "",
          county: "",
          village: "",
          cycle_duration: state.savings_cycle,
          share_value: parseFloat(state.share_value),
          status: "Active",
          country: transportedLocationData.country
            ? transportedLocationData.country.name
            : "",
          members: [],
          establishment_date: state.establishment_date,
          project_ids: validProjectIds,
          address: transportedLocationData,
          cycle: {
            shareValue: parseFloat(state.share_value),
          },
        }

        const secretary = {
          id: user.id,
          pubkey: uuid(),
          full_name: user.full_name,
          phone: user.phone_number.includes("+")
            ? user.phone_number
            : `+${user.phone_number}`,
          gender: user.gender,
          date_of_birth: user.date_of_birth.split("T")[0],
          member_role: "Secretary",
          status: "Active",
          loan_service_charge: state.secretary_loan_service_charge,
        }
        const data = {
          blockChain,
          group,
          secretary,
        }

        if (isConnected) {
          const isExist = dispatch(checkGroupName(group.description))
          if (isExist.data && isExist.data.status === "Active") {
            dispatch(createVslaError("duplicateGroup"))
            return
          }
        }

        props.setPayloadData(data)
        setState((prevState) => ({ ...prevState, errors: {} }))
        dispatch(setCurrentPage("group-addFunds"))
      }
    } catch (error) {
      console.log("Error Occured: ", error)
    }
  }

  const handleProjectChange = (selectedOptionId) => {
    if (!selectedOptionId?.length) {
      return
    }
    onChange({
      target: {
        value: selectedOptionId || [],
        name: "projects",
      },
    })
  }

  const { errors } = state

  return (
    <div>
      {isCreating ? (
        <StellarLoader msg={t({ id: "placeholder_copy.group_loader_msg" })} />
      ) : (
        <>
          <div
            style={{
              backgroundColor: "white",
              paddingTop: "34px",
              textAlign: "center",
            }}
          >
            <h4 className="textStyle" style={headerStyle}>
              <Trans id="title_copy.create_group" />
            </h4>
          </div>
          <Form onSubmit={onSubmit}>
            <Container>
              {stateGroup?.error && (
                <p style={{ color: "red", fontSize: "12" }}>{stateGroup.error}</p>
              )}
              {!!vslaError && (
                <ErrorMessage
                  msg={
                    vslaError === "duplicateGroup"
                      ? t({ id: "notifications.group_exist_error" })
                      : ""
                  }
                />
              )}
              <FormGroup>
                <span name="name_required_field_validation" style={{ color: "red" }}>
                  {errors.name}
                </span>
                <Input
                  type="text"
                  name="name"
                  style={formStyle}
                  placeholder={t({ id: "placeholder_copy.create_group_name" })}
                  onChange={onChange}
                  value={state.name}
                />
              </FormGroup>
              {!["ar", "lb"].includes(i18n.locale) &&
                !["لبنان", "Lebanon"].includes(state.country) && (
                  <FormGroup>
                    <Label style={loadingStyle}>
                      <Trans id="dropdowns.establishment_date" />
                    </Label>
                    <span name="establishment_date_required_field_validation" style={{ color: "red", display: "block", marginBottom: "5px" }}>
                      {errors.establishment_date}
                    </span>
                    <Input
                      type="date"
                      name="establishment_date"
                      selected={state.establishment_date}
                      defaultValue={state.establishment_date}
                      style={formStyle}
                      onChange={onChange}
                      numberofmonths={1}
                      max={new Date().toISOString().slice(0, 10)}
                    />
                  </FormGroup>
                )}
              <LocationForm
                {...(stateGroup?.address
                  ? {
                    preset: stateGroup?.address,
                  }
                  : { defaultCountry })}
                defaultCountry={defaultCountry}
                validateData={state.showLocationError}
              />
              <FormGroup>
                <ProjectSelector
                  onChange={handleProjectChange}
                  selectorOptions={
                    transportedLocationData.country
                      ? projects.filter(
                        (project) =>
                          project.country === transportedLocationData.country.name,
                      )
                      : []
                  }
                  placeholderText={t({ id: "placeholder_copy.project_name" })}
                />
              </FormGroup>
              {["ar", "lb"].includes(i18n.locale) && (
                <FormGroup
                  style={{
                    marginTop:
                      state.projects.length === 2
                        ? "45px"
                        : state.projects.length > 2
                          ? "85px"
                          : "0px",
                  }}
                >
                  <span
                    name="currency_required_field_validation"
                    style={{ color: "red" }}
                  >
                    {errors.currency}
                  </span>
                  <Input
                    id="currency"
                    type="select"
                    name="currency"
                    style={formStyle}
                    onChange={onChange}
                    value={state.currency}
                  >
                    <option value="">
                      <Trans id="placeholder_copy.currency" />
                    </option>
                    <option value="USD">
                      <Trans id="dropdowns.USD" />
                    </option>
                    <option value="LBP">
                      <Trans id="dropdowns.LBP" />
                    </option>
                  </Input>
                </FormGroup>
              )}

              {!["ar", "lb"].includes(i18n.locale) && (
                <FormGroup
                  style={{
                    marginTop:
                      state.projects.length === 2
                        ? "45px"
                        : state.projects.length > 2
                          ? "85px"
                          : "0px",
                  }}
                >
                  <span
                    name="savings_cycle_required_field_validation"
                    style={{ color: "red" }}
                  >
                    {errors.savings_cycle}
                  </span>
                  <Input
                    id="savings_cycle"
                    type="select"
                    name="savings_cycle"
                    style={formStyle}
                    onChange={onChange}
                    value={state.savings_cycle}
                  >
                    <option value="">
                      <Trans id="placeholder_copy.savings_cycle" />
                    </option>
                    <option value="6">
                      <Trans id="dropdowns.six_months" />
                    </option>
                    <option value="9">
                      <Trans id="dropdowns.nine_months" />
                    </option>
                    <option value="12">
                      <Trans id="dropdowns.year" />
                    </option>
                  </Input>
                </FormGroup>
              )}

              <FormGroup>
                <span
                  name="share_value_required_field_validation"
                  style={{ color: "red" }}
                >
                  {errors.share_value}
                </span>
                <Input
                  type="text"
                  name="share_value"
                  style={formStyle}
                  placeholder={t({
                    id: "placeholder_copy.create_group_share_value",
                  })}
                  onChange={onChange}
                  disabled={["لبنان", "Lebanon"].includes(state.country)}
                  value={state.share_value}
                />
              </FormGroup>
            </Container>
            <Container>
              <FormGroup>
                <span style={loadingStyle}>
                  <Trans id="placeholder_copy.secretary_pay_interest" />
                </span>
                <span style={{ color: "red" }}>{errors.paysInterest}</span>
                <Container
                  style={{ display: "flex", justifyContent: "space-around" }}
                >
                  <span>
                    <Input
                      id="payInterest"
                      type="radio"
                      name="secretary_loan_service_charge"
                      value={"true"}
                      checked={state.secretary_loan_service_charge}
                      onChange={onChange}
                    />

                    <span style={radioTextStyle}>
                      <Trans id="dropdowns.yes" />
                    </span>
                  </span>
                  <span>
                    <Input
                      id="noInterestPaid"
                      type="radio"
                      name="secretary_loan_service_charge"
                      value={"false"}
                      checked={!state.secretary_loan_service_charge}
                      onChange={onChange}
                    />
                    <span style={radioTextStyle}>
                      <Trans id="dropdowns.no" />
                    </span>
                  </span>
                </Container>
                <br />
              </FormGroup>
            </Container>
            <Container>
              <FormGroup>
                <Input
                  type="submit"
                  value={t({ id: "placeholder_copy.create_group_submit" })}
                  name="submit"
                  className="btn btn-primary"
                  disabled={vslaError}
                />
              </FormGroup>
            </Container>
          </Form>
        </>
      )}
    </div>
  )
}

const loadingStyle = {
  fontFamily: "Fira Sans",
  fontSize: "20px",
}

AddGroup.propTypes = {
  setPayloadData: PropTypes.func,
}

export default AddGroup
